import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { AvatarModule } from 'primeng/avatar';
import { SelectModule } from 'primeng/select';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  of,
} from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { StaffStoreService } from '@fitness-force/api';
import { StaffStatus } from '@fitness-force/enum';
import type { STAFFINTERFACE } from '@fitness-force/models';
import { ShortNamePipe } from '@fitness-force/pipes/short-name';
import { STAFF_STATE } from '@fitness-force/state';

const NO_STAFF_ID_FILTER = -1;
/** Fixed row height for p-select virtual scroll (must match item template). */
const STAFF_OPTION_ITEM_SIZE = 38;

@Component({
  selector: 'staff-selector[formGroup]',
  templateUrl: './assigned-to.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    SelectModule,
    ShortNamePipe,
    AvatarModule,
    TitleCasePipe,
  ],
})
export class AssignedToComponent implements OnInit, AfterViewInit, OnDestroy {
  fb = inject(UntypedFormBuilder);
  private readonly store = inject(Store);
  private readonly staffStoreService = inject(StaffStoreService);
  private readonly destroy$ = new Subject<void>();

  readonly formGroup = input<UntypedFormGroup>(new UntypedFormGroup({}));
  readonly controlName = input<UntypedFormControl>(new UntypedFormControl());
  readonly staffId$ = input(new BehaviorSubject<number>(0), { alias: 'id' });
  readonly styleClass = input<string>('border-none surface-50 w-full');
  readonly placeHolder = input<string>('Select');
  readonly claims$ = input<string[]>([], { alias: 'claims' });
  readonly showClear = input<boolean>(true);
  readonly canRepresetative$ = input(new BehaviorSubject(false), {
    alias: 'canRepresetative',
  });
  readonly staffIds$ = input<Observable<number[]>>(of([NO_STAFF_ID_FILTER]), {
    alias: 'filterstaff',
  });
  readonly selectedStaff = output<STAFFINTERFACE>({ alias: 'selectedStaff' });

  /** Options bound to p-select — keep select mounted; update array in place via signal. */
  readonly staffOptions = signal<STAFFINTERFACE[]>([]);
  readonly loading = signal(true);
  readonly virtualScrollItemSize = STAFF_OPTION_ITEM_SIZE;

  claims: string[] | Observable<string[]> = [];

  ngOnInit(): void {
    this.claims = this.claims$();
    this.bindStaffList();
  }

  ngAfterViewInit(): void {
    this.staffId$()
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.setSelectedStaff();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get SelectedStaff(): STAFFINTERFACE {
    return this.controlName().value;
  }

  onSelectStaff(event: STAFFINTERFACE): void {
    this.selectedStaff.emit(event);
  }

  private bindStaffList(): void {
    combineLatest([
      this.store.select(STAFF_STATE).pipe(
        map((state) => state?.staffList ?? []),
        distinctUntilChanged(),
      ),
      this.canRepresetative$().pipe(distinctUntilChanged()),
      this.staffIds$().pipe(
        distinctUntilChanged((prev, next) =>
          this.areStaffIdFiltersEqual(prev, next),
        ),
      ),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([staffList, canBeRepresentative, staffIds]) => {
        const filtered = this.filterStaffList(
          staffList,
          !!canBeRepresentative,
          staffIds,
        );
        this.staffOptions.set(filtered);
        this.loading.set(false);
        this.setSelectedStaff();
      });
  }

  private filterStaffList(
    staffList: STAFFINTERFACE[],
    canBeRepresentative: boolean,
    staffIds: number[] | null | undefined,
  ): STAFFINTERFACE[] {
    if (!staffList?.length) {
      return [];
    }

    // ID filter takes precedence (matches previous sequential override behavior).
    if (staffIds?.length && staffIds[0] !== NO_STAFF_ID_FILTER) {
      const allowedIds = new Set(staffIds);
      return staffList.filter((staff) => allowedIds.has(staff.id));
    }

    if (canBeRepresentative) {
      return staffList.filter(
        (staff) => this.isActiveStaff(staff) && staff.can_be_client_rep,
      );
    }

    return staffList.filter((staff) => this.isActiveStaff(staff));
  }

  private isActiveStaff(staff: STAFFINTERFACE): boolean {
    const status = staff.status as unknown;
    if (
      status === StaffStatus.Active ||
      status === 'Active' ||
      status === 'active'
    ) {
      return true;
    }
    return String(status).toLowerCase() === 'active';
  }

  private areStaffIdFiltersEqual(
    prev: number[] | null | undefined,
    next: number[] | null | undefined,
  ): boolean {
    if (prev === next) {
      return true;
    }
    if (!prev || !next || prev.length !== next.length) {
      return false;
    }
    for (let i = 0; i < prev.length; i++) {
      if (prev[i] !== next[i]) {
        return false;
      }
    }
    return true;
  }

  private setSelectedStaff(): void {
    const selectedId = this.staffId$()?.value;
    if (!selectedId || selectedId <= 0) {
      return;
    }

    const existing = this.staffOptions().find(
      (staff) => staff.id === selectedId,
    );
    if (existing?.id && existing.id > 0) {
      this.controlName().setValue(existing);
      return;
    }

    this.staffStoreService
      .getStaffById(selectedId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((staff) => {
        if (!staff?.id) {
          return;
        }
        const alreadyPresent = this.staffOptions().some(
          (s) => s.id === staff.id,
        );
        if (!alreadyPresent) {
          this.staffOptions.update((list) => [...list, staff]);
        }
        this.controlName().setValue(staff);
      });
  }
}
